import { Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NeptingPaymentService } from './NeptingPaymentService.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { numberD, numberToCurrency, paymentFromOrders, sleep } from 'src/smoothr-web-app-core/utils/utils';
import { ModalController } from '@ionic/angular';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import Order from 'src/smoothr-web-app-core/models/Order';
import Customer from 'src/smoothr-web-app-core/models/Customer';
import { PaymentMethod } from 'src/smoothr-web-app-core/enums/PaymentMethod';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import { PaymentModalResult } from 'src/smoothr-web-app-core/components/payment-modal/payment-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnalyticsService } from 'src/smoothr-web-app-core/services/analytics/analytics.service';
import { Api } from 'src/smoothr-web-app-core/api/api';

// @ts-ignore
const applePayAvailable = !!window.ApplePaySession;

@Component({
	selector: 'app-nepting-payment',
	templateUrl: './nepting-payment.page.html',
	styleUrls: ['./nepting-payment.page.scss'],
	standalone: false,
})
export class NeptingPaymentModalComponent implements OnInit, OnDestroy {
	static url = 'nepting-payment';

	payment: Payment;
	paymentMethods: PaymentMethod[];
	venue: Venue;
	order: Order;
	customer: Customer;

	selectedPaymentMethod: PaymentMethod = null;
	sdkReady = false;
	googleSdkReady = false;
	appleSdkReady = false;

	loading = false;
	paymentProcessing = false;
	error: string | null = null;

	PaymentMethod = PaymentMethod;
	numberToCurrency = numberToCurrency;

	private messageHandler = (event: MessageEvent) => this.handleAppleMessage(event);

	public nepting = inject(NeptingPaymentService);
	private destroyRef = inject(DestroyRef);
	private modalCtrl = inject(ModalController);
	private translate = inject(TranslateService);
	private snackBarCtrl = inject(MatSnackBar);
	private analytics = inject(AnalyticsService);
	constructor() {}

	static async show(modalCtrl: ModalController, venue: Venue, order: Order, customer: Customer, tip: number = 0) {
		if (!venue.neptingEnabled) {
			console.error('❌ Nepting payment not enabled for venue', venue._id);
			return null;
		}
		const paymentMethods: PaymentMethod[] = [];
		if (venue.ccEnabled) {
			paymentMethods.push(PaymentMethod.CREDIT_CARD);
		}
		if (venue.gpEnabled) {
			paymentMethods.push(PaymentMethod.GPAY);
		}
		if (venue.apEnabled && applePayAvailable) {
			paymentMethods.push(PaymentMethod.APAY);
		}

		const modal = await modalCtrl.create({
			component: NeptingPaymentModalComponent,
			componentProps: {
				payment: paymentFromOrders([order], tip),
				paymentMethods,
				venue,
				order,
				customer,
			},
			backdropDismiss: false,
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data;
	}

	ngOnInit() {
		this.nepting.formValid$.next(false);

		this.nepting.hostedToken$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(token => {
			if (!token) return;
			this.handleHostedToken(token);
		});
	}

	changeSelectedPaymentMethod(method: PaymentMethod) {
		this.selectedPaymentMethod = method;
		this.loadPayment();
	}

	/** GENERAL PAYMENT LOADING */
	async loadPayment() {
		if (this.loading) return;
		this.loading = true;

		this.nepting.destroyHostedFields();

		this.payment._id = null;
		this.payment.method = this.selectedPaymentMethod;

		try {
			this.payment = await Api.createPayment(this.payment);
			this.initializeSDKLoader(this.selectedPaymentMethod);
			this.analytics.paymentLoaded(this.order, this.selectedPaymentMethod);
		} catch {
			this.handleLoadError();
		} finally {
			this.loading = false;
		}
	}

	/** SDK LOADING */
	private initializeSDKLoader(method: PaymentMethod) {
		const sdkMap = {
			[PaymentMethod.CREDIT_CARD]: this.nepting.sdkReady$,
			[PaymentMethod.GPAY]: this.nepting.sdkGoogleReady$,
			[PaymentMethod.APAY]: this.nepting.sdkAppleReady$,
		};

		sdkMap[method]
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(async ready => ready && (await this.initializeSDK(method)));
	}

	/** SDK INIT LOGIC */
	private async initializeSDK(method: PaymentMethod) {
		await sleep(300);

		switch (method) {
			case PaymentMethod.CREDIT_CARD:
				return this.initCreditCard();
			case PaymentMethod.GPAY:
				return this.initGooglePay();
			case PaymentMethod.APAY:
				return this.initApplePay();
		}
	}

	/** CREDIT CARD */
	private initCreditCard() {
		const brand = this.venue.nepting.brandId;
		const merchant = this.venue.nepting.merchantId;
		const trx = this.payment.neptingPayment.transactionId;

		this.nepting.configureTransaction(brand, merchant, trx);

		const fields = {
			holder: { id: 'holder-container', placeholder: 'L. Ibanez', framewidth: '100%', frameheight: 60 },
			card: { id: 'card-container', placeholder: '1234 5678 9012 3456', framewidth: '100%', frameheight: 85 },
			expiry: { id: 'expiry-container', placeholder: 'MM/YY', framewidth: '100%', frameheight: 60 },
			cryptogram: { id: 'cvv-container', placeholder: '3 digits', framewidth: '100%', frameheight: 60 },
		};

		this.nepting.loadHostedFields(fields, (isValid: boolean) => this.nepting.formValid$.next(isValid));
	}

	/** GOOGLE PAY INIT */
	private initGooglePay() {
		const sdk = window.NeptingGooglePaySDK;
		if (!sdk) return;

		const { brandId, merchantId } = this.venue.nepting;
		const { transactionId, request } = this.payment.neptingPayment;
		const origin = window.location.hostname;

		Object.assign(sdk, {
			locale: this.translate.currentLang,
			amount: request.amount,
			merchantLabel: request.merchantLabel,
			currencyAlpha: this.venue.currency,
			brandId,
			merchantId,
			transactionId,
			merchantWebsite: origin,
			clientOrigin: origin,
		});

		sdk.googlePayButtonDisplayedCallback = () => (this.loading = false);
		sdk.googlePaymentEndedCallback = (data: any) => this.handleGoogleToken(data);

		sdk.addGooglePayButton('google-pay-container', '100%', '55');
	}

	/** APPLE PAY INIT */
	private initApplePay() {
		const sdk = window.NeptingApplePaySDK;
		if (!sdk) return;

		const { brandId, merchantId } = this.venue.nepting;
		const { transactionId, request } = this.payment.neptingPayment;
		const origin = window.location.hostname;

		Object.assign(sdk, {
			locale: this.translate.currentLang,
			amount: request.amount,
			merchantLabel: request.merchantLabel,
			currencyAlpha: this.venue.currency,
			brandId,
			merchantId,
			transactionId,
			merchantWebsite: origin,
			clientOrigin: origin,
		});

		sdk.applePayButtonDisplayedCallback = () => (this.loading = false);
		sdk.applePaymentEndedCallback = token => this.handleAppleToken(token);

		window.addEventListener('message', this.messageHandler);
		sdk.addApplePayButton('apple-pay-container', '100%', '55');
	}

	/** TOKEN HANDLERS */
	private createBackendRequest() {
		return {
			...this.payment.neptingPayment.request,
			nep_Sign: this.payment.neptingPayment.request.signature,
			brandId: this.nepting.base64Encode(this.venue.nepting.brandId),
		};
	}

	private handleHostedToken(res: any) {
		if (!res || res.tokenizationStatus !== 'success') {
			console.log(res);
			this.error = 'Card validation failed';
			return;
		}

		this.paymentProcessing = true;
		const payload = {
			...this.createBackendRequest(),
			nep_HFieldsToken: res.hfieldsToken,
			hfieldsToken: res.hfieldsToken,
		};

		this.nepting.redirectToNeptingRaw(payload);
	}

	private handleGoogleToken(data: any) {
		if (!data?.paymentMethodData) {
			this.error = 'Failed to get payment token.';
			return;
		}

		const encoded = this.nepting.base64Encode(JSON.stringify(data));

		const payload = {
			...this.createBackendRequest(),
			nep_GooglePayCardToken: encoded,
			googlePayCardToken: encoded,
		};

		this.nepting.redirectToNeptingRaw(payload);
	}

	private async handleAppleToken(paymentToken: any) {
		this.paymentProcessing = true;

		const encrypted = this.nepting.base64Encode(JSON.stringify(paymentToken));

		const payload = {
			...this.createBackendRequest(),
			applePayCardToken: encrypted,
		};

		await sleep(50);
		this.nepting.redirectToNeptingRaw(payload);
	}

	/** APPLE POSTMESSAGE */
	private handleAppleMessage(event: MessageEvent) {
		const sdk = window.NeptingApplePaySDK;
		if (event.data?.messageType === 'applepayPaymentAuthorized') {
			sdk?.applePaymentEndedCallback?.(event.data.paymentToken);
		}
	}

	onSubmit(e: Event) {
		e.preventDefault();
		if (this.paymentProcessing || !this.nepting.formValid$.getValue()) {
			this.error = 'Form invalid';
			return;
		}
		this.nepting.collectCardData();
	}

	private handleLoadError() {
		this.snackBarCtrl.open(this.translate.instant('payment_modal.error'));
		this.analytics.paymentLoadingFailed(this.order, this.selectedPaymentMethod);
		this.selectedPaymentMethod = null;
	}

	dismiss() {
		this.modalCtrl.dismiss({ result: PaymentModalResult.DISMISS });
	}

	ngOnDestroy() {
		window.removeEventListener('message', this.messageHandler);
		this.nepting.destroyHostedFields();
	}
}
